import React, { useContext, useState } from 'react';
import { useQuery } from '@apollo/client';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { shortOrdersQuery } from '../../graphql/queries';
import { getAuth0Url, isAnonUser } from '@/utils/user';
import { centsToPrice } from '@/utils/formatter';
import {
  EditSection,
  LoadingContainer,
  ResponsiveTable,
} from '../../components/Layout';
import {
  CheckBoxContainer,
  CheckBoxLabel,
} from '../../pages/AdminStoreOrders/styled';
import { Checkbox } from 'primereact/checkbox';
import {
  CanAccessPartnerData,
  ViewerContext,
} from '../../providers/ViewerContextProvider';
import { FaDownload, FaLock } from 'react-icons/fa';
import i18n, { getDateFormatOptions } from '@/utils/i18n';
import { getDateFromISOTime, getYearsFrom } from '@/utils/time';
import { IconButton } from '../../components/Button/Button';
import { toCsvBlob } from '@/utils/data';
import { DEFAULT_CURRENCY } from '@/utils/price';
import { ContentSection } from '../../components/Layout/ContentSection';
import { Routes } from '@/utils/routes';

type MerchantOrdersTabProps = {
  externalPickpackId: string | null;
  title: string;
};

const initialYear = new Date().getFullYear().toString();
const initialMonth = (new Date().getMonth() + 1).toString();

export const MerchantOrdersTab: React.FC<MerchantOrdersTabProps> = function ({
  externalPickpackId,
  title,
}) {
  const [selectedYear, setSelectedYear] = useState(initialYear);
  const [selectedMonth, setSelectedMonth] = useState(initialMonth);
  const viewer = useContext(ViewerContext);
  const allowedToAccess = CanAccessPartnerData(viewer);
  const [showAll, setShowAll] = useState(false);
  const [groupByDays, setGroupByDays] = useState(false);
  const timespanFrom = showAll
    ? moment('2016-01-01')
    : moment(`${selectedYear}-${selectedMonth}-01`);
  const timespanTo = showAll
    ? moment(`${initialYear}-${initialMonth}-01`).add(1, 'year')
    : moment(`${selectedYear}-${selectedMonth}-01`).endOf('month');
  const { data, loading } = useQuery(shortOrdersQuery, {
    variables: {
      store_id: externalPickpackId,
      timespan_from: timespanFrom.toISOString(),
      timespan_to: timespanTo.toISOString(),
      include_external: true,
    },
  });

  let { orders, store } = data || { orders: [], store: {} };
  const storeCurrency = store.currency || DEFAULT_CURRENCY;

  function reduceOrder(order: any) {
    // @ts-ignore
    const pickupTimeFormatted =
      order.type === 'INSTANT_ORDER'
        ? i18n.t('Order:asap')
        : new Date(order.pickup_time).toLocaleDateString(
            i18n.language,
            getDateFormatOptions(true),
          );
    // @ts-ignore
    const {
      name: userName,
      email: userEmail,
      external_id: externalUserId,
    } = order.user || {};

    let vytalUserId = (externalUserId || '').replace('vytal_', '');
    const reducedOrder: any = {};
    reducedOrder[i18n.t('Order:pickupCode')] = order.code_text;
    reducedOrder[i18n.t('Common:status')] = i18n.t(
      `Order:Status:${order.status}`,
    );
    reducedOrder[i18n.t('Order:orderAmount')] = centsToPrice(
      order.total,
      (order.store || {}).currency || DEFAULT_CURRENCY,
    );
    reducedOrder[i18n.t('Order:storeAmount')] = centsToPrice(
      order.store_total,
      (order.store || {}).currency || DEFAULT_CURRENCY,
    );
    reducedOrder[i18n.t('Order:pickupTime')] = pickupTimeFormatted;
    reducedOrder[i18n.t('Order:platform')] = vytalUserId ? 'VYTAL' : 'pickpack';
    if (allowedToAccess) {
      reducedOrder['User'] = userName || userEmail;
    }
    return reducedOrder;
  }

  async function downloadAsCsv() {
    const blob = toCsvBlob(orders.map(reduceOrder));
    const a = window.document.createElement('a');
    a.href = window.URL.createObjectURL(blob);
    a.download = `Orders_${timespanFrom.toISOString()}_to_${timespanTo.toISOString()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  if (loading) {
    return <LoadingContainer />;
  }

  if (groupByDays && orders.length > 0) {
    const ordersWithSumEntry = [];
    let currentDate = getDateFromISOTime(orders[0].pickup_time);
    let currentStoreTotal = 0;
    let currentOrderCount = 0;
    for (const order of orders) {
      if (getDateFromISOTime(order.pickup_time) != currentDate) {
        ordersWithSumEntry.push({
          isDayGroupEntry: true,
          date: currentDate,
          storeTotal: currentStoreTotal,
          orderCount: currentOrderCount,
        });
        currentStoreTotal = 0;
        currentOrderCount = 0;
      }

      ordersWithSumEntry.push(order);
      currentStoreTotal += order.store_total;
      currentDate = getDateFromISOTime(order.pickup_time);
      currentOrderCount += 1;
    }

    // add last group
    ordersWithSumEntry.push({
      isDayGroupEntry: true,
      date: currentDate,
      storeTotal: currentStoreTotal,
      orderCount: currentOrderCount,
    });
    orders = ordersWithSumEntry;
  }

  return (
    <ContentSection title={title} collapsable={false} key="content">
      <EditSection title={i18n.t('Order:orders')}>
        {orders && (
          <>
            <div className="row">
              <div className="col-sm">
                <label htmlFor="invoiceYearSelect">
                  {i18n.t('Common:year')}
                </label>
                <select
                  className="form-control"
                  id="invoiceYearSelect"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  disabled={showAll}
                >
                  {getYearsFrom('2020').map((y) => (
                    <option value={y}>{y}</option>
                  ))}
                </select>
              </div>
              <div className="col-sm">
                <label htmlFor="invoiceMonthSelect">
                  {i18n.t('Common:month')}
                </label>
                <select
                  className="form-control"
                  id="invoiceMonthSelect"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  disabled={showAll}
                >
                  {Array.from(Array(12).keys()).map((m) => (
                    <option value={m + 1}>
                      {i18n.t(`Common:Month:${m + 1}`)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-sm">
                <IconButton
                  icon={<FaDownload />}
                  label={'Export as CSV'}
                  onClick={downloadAsCsv}
                  style={{ float: 'right' }}
                />
              </div>
            </div>
            <CheckBoxContainer
              className="mt-4"
              onClick={() => setShowAll(!showAll)}
            >
              <Checkbox checked={showAll} />
              <CheckBoxLabel>{` ${i18n.t('Order:showAll')}`}</CheckBoxLabel>
            </CheckBoxContainer>
            <CheckBoxContainer
              className="mt-4"
              onClick={() => setGroupByDays(!groupByDays)}
            >
              <Checkbox checked={groupByDays} />
              <CheckBoxLabel>{` ${i18n.t('Order:groupByDays')}`}</CheckBoxLabel>
            </CheckBoxContainer>
            {orders.length === 0 && (
              <p className="mt-4">{i18n.t('Order:noOrdersFound')}</p>
            )}
            {orders.length > 0 && (
              <>
                <ResponsiveTable className="mt-4">
                  <thead>
                    <tr>
                      {groupByDays && <th>{i18n.t('Order:day')}</th>}
                      <th>{i18n.t('Order:pickupCode')}</th>
                      <th>{i18n.t('Common:status')}</th>
                      <th>{i18n.t('Order:orderAmount')}</th>
                      <th>{i18n.t('Order:storeAmount')}</th>
                      <th>{i18n.t('Order:pickupTime')}</th>
                      <th>{i18n.t('Order:platform')}</th>
                      {allowedToAccess && (
                        <th>
                          <FaLock size="13" /> User
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order: any) => {
                      if (order.isDayGroupEntry) {
                        return (
                          <tr key={order.date}>
                            <td colSpan={1}>
                              <b>
                                {new Date(order.date).toLocaleDateString(
                                  i18n.language,
                                  getDateFormatOptions(false),
                                )}
                              </b>
                            </td>
                            <td colSpan={2}>&nbsp;</td>
                            <td colSpan={1}>
                              <b>
                                {i18n.t('Order:sum')} ({order.orderCount}
                                ):
                              </b>
                            </td>
                            <td colSpan={6}>
                              <b>
                                {centsToPrice(order.storeTotal, storeCurrency)}
                              </b>
                            </td>
                          </tr>
                        );
                      } else {
                        const style = order.external_id
                          ? {
                              backgroundColor: '#d4d4d4',
                              opacity: 1,
                            }
                          : {};
                        // @ts-ignore
                        const pickupTimeFormatted =
                          order.type === 'INSTANT_ORDER'
                            ? i18n.t('Order:asap')
                            : new Date(order.pickup_time).toLocaleDateString(
                                i18n.language,
                                getDateFormatOptions(true),
                              );
                        // @ts-ignore
                        const {
                          id: userId,
                          name: userName,
                          email: userEmail,
                          external_id: externalUserId,
                        } = order.user || {};

                        let vytalUserId = (externalUserId || '').replace(
                          'vytal_',
                          '',
                        );

                        return (
                          <tr key={order.id} style={style}>
                            {groupByDays && <td>&nbsp;</td>}
                            <td>
                              <Link to={`/pre-order/${order.id}`}>
                                {order.code_text}
                              </Link>
                            </td>
                            <td title={order.status}>
                              {i18n.t(`Order:Status:${order.status}`)}
                            </td>
                            <td>
                              {centsToPrice(
                                order.total,
                                (order.store || {}).currency ||
                                  DEFAULT_CURRENCY,
                              )}
                            </td>
                            <td>
                              <b>
                                {centsToPrice(
                                  order.store_total,
                                  (order.store || {}).currency ||
                                    DEFAULT_CURRENCY,
                                )}
                              </b>
                            </td>
                            <td>{pickupTimeFormatted}</td>
                            <td>{vytalUserId ? 'VYTAL' : 'pickpack'}</td>
                            {allowedToAccess && (
                              <td>
                                {vytalUserId ? (
                                  <Link to={Routes.users(vytalUserId)}>
                                    {userName || userEmail}
                                  </Link>
                                ) : (
                                  <span>{userName || userEmail}</span>
                                )}{' '}
                                {userId &&
                                  !vytalUserId &&
                                  !isAnonUser(userId) && (
                                    <a
                                      href={getAuth0Url(userId)}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      (Auth0)
                                    </a>
                                  )}
                              </td>
                            )}
                          </tr>
                        );
                      }
                    })}
                  </tbody>
                </ResponsiveTable>
              </>
            )}
          </>
        )}
      </EditSection>
    </ContentSection>
  );
};
